import * as Yup from 'yup';
import { isValid as isValidPostCode } from 'postcode';

import { errorMessages } from './strings';
import fieldTypes from './field-types';
import { capitaliseString } from '../../utils/content-formatting';

export const parseStringTemplate = (str, obj) => {
  const parts = str.split(/\$\{(?!\d)[\wæøåÆØÅ]*\}/);
  const args = str.match(/[^{}]+(?=})/g) || [];
  const parameters = args.map((argument) =>
    obj[argument] === undefined ? '' : obj[argument]
  );
  return String.raw({ raw: parts }, ...parameters);
};

const phoneNumberLength = (val) => val && val.toString().length > 9;

// checks that the date entered is valid by creating a date object
// from the parts and then checking that none of the date parts have
// rolled over, f.e 32 january -> 1st of february
const validDate = (year, month, day) => {
  const date = new Date(Date.UTC(year, month, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month &&
    date.getUTCDate() === day
  );
};

export default {
  [fieldTypes.DROPDOWN]: (required, label) =>
    required
      ? Yup.string()
          .required(
            capitaliseString(
              parseStringTemplate(errorMessages.REQUIRED, { label })
            )
          )
          .min(1)
      : Yup.string(),
  [fieldTypes.FUNDRAISING_EVENTS]: (required, label) =>
    required
      ? Yup.string()
          .required(
            capitaliseString(
              parseStringTemplate(errorMessages.REQUIRED, { label })
            )
          )
          .min(1)
      : Yup.string(),
  // Textfield and Text area
  [fieldTypes.TEXT_AREA]: (required, label) =>
    required
      ? Yup.string().required(
          capitaliseString(
            parseStringTemplate(errorMessages.REQUIRED, { label })
          )
        )
      : Yup.string(),
  [fieldTypes.TEXT_FIELD]: (required, label) =>
    required
      ? Yup.string().required(
          capitaliseString(
            parseStringTemplate(errorMessages.REQUIRED, { label })
          )
        )
      : Yup.string(),
  // Address
  [fieldTypes.ADDRESS]: (required) =>
    required
      ? Yup.object({
          address1: Yup.string()
            .max(255, errorMessages.ADDRESS_INVALID)
            .required(errorMessages.ADDRESS_REQUIRED),
          address2: Yup.string(),
          city: Yup.string().required(errorMessages.CITY_REQUIRED),
          postcode: Yup.string()
            .required(errorMessages.POSTCODE_REQUIRED)
            .test(
              'len',
              errorMessages.POSTCODE_INVALID,
              (val) => val && isValidPostCode(val)
            ),
        })
      : Yup.object({
          address1: Yup.string(),
          address2: Yup.string(),
          city: Yup.string(),
          postcode: Yup.string().test(
            'len',
            errorMessages.POSTCODE_INVALID,
            (val) => !val || (val && isValidPostCode(val))
          ),
        }),
  [fieldTypes.ADDRESS_INTERNATIONAL]: (required) =>
    required
      ? Yup.object({
          address1: Yup.string()
            .max(255, errorMessages.ADDRESS_INVALID)
            .required(errorMessages.ADDRESS_REQUIRED),
          address2: Yup.string(),
          city: Yup.string().required(errorMessages.CITY_REQUIRED),
          country: Yup.string().required(errorMessages.COUNTRY_REQUIRED),
          postcode: Yup.string().required(
            errorMessages.POSTCODE_REQUIRED_INTERNATIONAL
          ),
        })
      : Yup.object({
          address1: Yup.string(),
          address2: Yup.string(),
          city: Yup.string(),
          country: Yup.string(),
          postcode: Yup.string(),
        }),
  // Postcode
  [fieldTypes.POSTCODE]: (required) =>
    required
      ? Yup.string()
          .required(errorMessages.POSTCODE_REQUIRED)
          .test(
            'len',
            errorMessages.POSTCODE_INVALID,
            (val) => val && isValidPostCode(val)
          )
      : Yup.string().test(
          'len',
          errorMessages.POSTCODE_INVALID,
          (val) => !val || (val && isValidPostCode(val))
        ),
  // Checkboxes
  [fieldTypes.CHECKBOXES]: (required, label, { options }) => {
    if (options && options.length > 1) {
      if (required) {
        return Yup.array().min(
          1,
          capitaliseString(
            parseStringTemplate(errorMessages.CHECKBOXES_REQUIRED, { label })
          )
        );
      }
      return Yup.array();
    }
    if (required) {
      const errorContent = options[0]?.label || label;
      return Yup.boolean()
        .oneOf(
          [true],
          capitaliseString(
            parseStringTemplate(errorMessages.CHECKBOX_REQUIRED, {
              label: errorContent,
            })
          )
        )
        .required(
          capitaliseString(
            parseStringTemplate(errorMessages.CHECKBOX_REQUIRED, {
              label: errorContent,
            })
          )
        );
    }
    return Yup.array().nullable();
  },
  // Radio buttons
  [fieldTypes.RADIO_BUTTONS]: (required, lowercaseLabel, { label }) => {
    if (required) {
      return Yup.string().required(
        capitaliseString(
          parseStringTemplate(errorMessages.RADIOS_REQUIRED, {
            label,
          })
        )
      );
    }
  },
  // Star rating
  [fieldTypes.STAR_RATING]: (required, lowercaseLabel, { label }) => {
    if (required) {
      return Yup.string().required(
        capitaliseString(
          parseStringTemplate(errorMessages.STAR_RATING_REQUIRED, {
            label,
          })
        )
      );
    }
  },
  // Email
  [fieldTypes.EMAIL]: (required) =>
    required
      ? Yup.string()
          .email(errorMessages.EMAIL_INVALID)
          .required(errorMessages.EMAIL_REQUIRED)
      : Yup.string().email(errorMessages.EMAIL_INVALID),
  // Phone number (UK)
  [fieldTypes.PHONE_NUMBER]: (required) =>
    required
      ? Yup.number()
          .typeError(errorMessages.PHONE_NUMBER_INVALID)
          .test('len', errorMessages.PHONE_NUMBER_TOO_SHORT, phoneNumberLength)
          .required(errorMessages.PHONE_NUMBER_REQUIRED)
      : Yup.number()
          .typeError(errorMessages.PHONE_NUMBER_INVALID)
          .test(
            'len',
            errorMessages.PHONE_NUMBER_TOO_SHORT,
            (val) => !val || (val && phoneNumberLength(val))
          ),
  // International phone number - allows hyphens, spaces, parentheses and dashes
  [fieldTypes.PHONE_NUMBER_INTERNATIONAL]: (required) =>
    required
      ? Yup.string()
          .matches(
            /^\+?[0-9\-\s()]+$/,
            errorMessages.PHONE_NUMBER_INTERNATIONAL_INVALID
          )
          .required(errorMessages.PHONE_NUMBER_INTERNATIONAL_REQUIRED)
      : Yup.string().matches(
          /^\+?[0-9\-\s()]*$/,
          errorMessages.PHONE_NUMBER_INTERNATIONAL_INVALID
        ),
  // Number
  [fieldTypes.NUMBER]: (required) =>
    required
      ? Yup.number().required(errorMessages.NUMBER_REQUIRED)
      : Yup.number(),
  // Date of birth
  [fieldTypes.DATE_OF_BIRTH]: (required) =>
    required
      ? Yup.object({
          day: Yup.number()
            .required(errorMessages.DAY_MISSING)
            .min(1, errorMessages.DAY_INVALID)
            .max(31, errorMessages.DAY_INVALID),
          month: Yup.number()
            .required(errorMessages.MONTH_MISSING)
            .min(1, errorMessages.MONTH_INVALID)
            .max(12, errorMessages.MONTH_INVALID),
          year: Yup.number()
            .required(errorMessages.YEAR_MISSING)
            .test(
              'is_year',
              errorMessages.YEAR_INVALID,
              (value) => value && value.toString().length === 4
            ),
        }).test({
          name: 'is_valid_date',
          // eslint-disable-next-line object-shorthand
          test: function (value) {
            // check if any parts have been filled in
            if (required && !value.year && !value.month && !value.day) {
              throw this.createError({
                path: `${this.path}._`,
                message: errorMessages.DATE_OF_BIRTH_MISSING,
              });
            }

            // if one of the parts hasn't been filled in abort validation
            if (!value.year || !value.month || !value.day) {
              return true;
            }

            if (!validDate(value.year, value.month - 1, value.day)) {
              throw this.createError({
                path: `${this.path}._`,
                message: errorMessages.DATE_OF_BIRTH_INVALID,
              });
            }

            if (new Date(value.year, value.month - 1, value.day) > new Date()) {
              throw this.createError({
                path: `${this.path}._`,
                message: errorMessages.DATE_OF_BIRTH_IN_FUTURE,
              });
            }
            return true;
          },
        })
      : Yup.object({
          day: Yup.number()
            .min(1, errorMessages.DAY_INVALID)
            .max(31, errorMessages.DAY_INVALID),
          month: Yup.number()
            .min(1, errorMessages.MONTH_INVALID)
            .max(12, errorMessages.MONTH_INVALID),
          year: Yup.number().test(
            'is_year',
            errorMessages.YEAR_INVALID,
            (value) => !value || (value && value.toString().length === 4)
          ),
        }).test({
          name: 'is_valid_date',
          // eslint-disable-next-line object-shorthand
          test: function (value) {
            // if one of the parts hasn't been filled in abort validation
            if (!value.year || !value.month || !value.day) {
              return true;
            }

            if (!validDate(value.year, value.month - 1, value.day)) {
              throw this.createError({
                path: `${this.path}._`,
                message: errorMessages.DATE_OF_BIRTH_INVALID,
              });
            }

            if (new Date(value.year, value.month - 1, value.day) > new Date()) {
              throw this.createError({
                path: `${this.path}._`,
                message: errorMessages.DATE_OF_BIRTH_IN_FUTURE,
              });
            }
            return true;
          },
        }),
  // File Upload
  [fieldTypes.FILE_UPLOAD]: (required, label, { infoRedactionRequired }) =>
    Yup.lazy((value) =>
      Yup.object({
        files: required
          ? Yup.array()
              .of(Yup.mixed())
              .test({
                message: errorMessages.FILE_UPLOAD_REQUIRED,
                test: (arr) => arr.length,
              })
          : Yup.array().of(Yup.mixed()),
        redaction:
          // if info redaction is required but file upload is optional, only
          // require info redaction if one or more files has been uploaded
          infoRedactionRequired && (required || value.files.length)
            ? Yup.boolean()
                .oneOf([true], errorMessages.CHECKBOX_REQUIRED)
                .required(errorMessages.CHECKBOX_REQUIRED)
            : Yup.boolean(),
      })
    ),

  [fieldTypes.AUTO_SUGGEST]: (required, label, ...rest) => {
    const isValueInOptions = (value) => {
      const optionLabels = rest[0].options?.map((option) => option.label);

      return optionLabels.includes(value);
    };

    if (required) {
      return Yup.string()
        .required(
          capitaliseString(
            parseStringTemplate(errorMessages.AUTO_SUGGEST_REQUIRED, {
              label,
            })
          )
        )
        .test(
          'is_valid_option',
          parseStringTemplate(errorMessages.AUTO_SUGGEST_INVALID, { label }),
          (value) => isValueInOptions(value)
        );
    }

    return Yup.string().test(
      'is_valid_option_or_empty',
      parseStringTemplate(errorMessages.AUTO_SUGGEST_INVALID, { label }),
      (value) => {
        // if value is undefined, it means that the user has not selected an option, which is valid as the field is optional
        if (value === undefined) {
          return true;
        }

        // If a value has been submitted, validate it against the options

        return isValueInOptions(value);
      }
    );
  },
};
